/**
 * Image Utilities
 *
 * Utility functions for image processing, blur placeholders, and responsive calculations
 */
import { ASPECT_RATIO_MAP } from './ResponsiveImage.types';
/**
 * Generate a simple blur placeholder URL from an image URL
 * This is a fallback when blurDataURL is not provided
 */
export function generateBlurPlaceholder(imageUrl) {
    // For production, this would generate a tiny base64 blur image
    // For now, return a simple gray placeholder
    if (typeof document === 'undefined') {
        // SSR fallback - return static placeholder
        return 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAgAAAAICAYAAADED76LAAAABHNCSVQICAgIfAhkiAAAAAlwSFlzAAAAdgAAAHYBTnsmCAAAABl0RVh0U29mdHdhcmUAd3d3Lmlua3NjYXBlLm9yZ5vuPBoAAAAXSURBVBiVY2RgYPgPBEwMDAyMDIwMjAAADQABAAFRjOAAAAAASUVORK5CYII=';
    }
    const canvas = document.createElement('canvas');
    canvas.width = 8;
    canvas.height = 8;
    const ctx = canvas.getContext('2d');
    if (ctx) {
        ctx.fillStyle = '#f3f4f6'; // gray-100
        ctx.fillRect(0, 0, 8, 8);
    }
    return canvas.toDataURL('image/png');
}
/**
 * Preload an image and return a promise
 */
export function preloadImage(src) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve();
        img.onerror = reject;
        img.src = src;
    });
}
/**
 * Calculate optimal image dimensions for responsive loading
 */
export function calculateResponsiveDimensions(containerWidth, aspectRatio) {
    const height = containerWidth / (typeof aspectRatio === 'number' ? aspectRatio : getNumericRatio(aspectRatio));
    return {
        width: Math.round(containerWidth),
        height: Math.round(height),
    };
}
/**
 * Calculate padding-bottom percentage for aspect ratio containers
 */
export function calculateAspectRatioPadding(aspectRatio) {
    if (typeof aspectRatio === 'number') {
        if (aspectRatio <= 0) {
            throw new Error('AspectRatio must be greater than 0');
        }
        return (1 / aspectRatio) * 100;
    }
    const ratio = ASPECT_RATIO_MAP[aspectRatio];
    return (1 / ratio) * 100;
}
/**
 * Get CSS aspect-ratio value for modern browsers
 */
export function getAspectRatioValue(aspectRatio) {
    if (typeof aspectRatio === 'number') {
        if (aspectRatio <= 0) {
            throw new Error('AspectRatio must be greater than 0');
        }
        return `${aspectRatio}`;
    }
    const ratio = ASPECT_RATIO_MAP[aspectRatio];
    return `${ratio}`;
}
/**
 * Convert aspect ratio string to numeric value
 */
function getNumericRatio(aspectRatio) {
    if (typeof aspectRatio === 'number') {
        return aspectRatio;
    }
    return ASPECT_RATIO_MAP[aspectRatio];
}
/**
 * Generate srcset for responsive images
 */
export function generateSrcSet(src, widths = [320, 640, 1024, 1280, 1920]) {
    return widths.map((width) => `${src}?w=${width} ${width}w`).join(', ');
}
/**
 * Check if an image URL is external (not relative)
 */
export function isExternalImage(src) {
    return /^https?:\/\//.test(src);
}
/**
 * Validate image source URL
 */
export function validateImageSrc(src) {
    if (!src || typeof src !== 'string')
        return false;
    try {
        new URL(src);
        return true;
    }
    catch {
        // Allow relative URLs
        return src.length > 0;
    }
}
/**
 * Get container styles for aspect ratio
 */
export function getAspectRatioContainerStyles(aspectRatio) {
    const paddingBottom = calculateAspectRatioPadding(aspectRatio);
    return {
        position: 'relative',
        width: '100%',
        paddingBottom: `${paddingBottom}%`,
        overflow: 'hidden',
    };
}
/**
 * Get image styles for aspect ratio container
 */
export function getAspectRatioImageStyles() {
    return {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    };
}
/**
 * Handle image load errors with fallback
 */
export function handleImageError(event, fallbackSrc, onError) {
    const img = event.currentTarget;
    if (fallbackSrc && img.src !== fallbackSrc) {
        img.src = fallbackSrc;
        return;
    }
    // Set a default fallback if none provided
    img.src =
        'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjI0IiBoZWlnaHQ9IjI0IiBmaWxsPSIjZjNmNGY2Ii8+CjxwYXRoIGQ9Ik0xMiAxMk0xMiAxMnoiIHN0cm9rZT0iIzk3OTdhNyIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz4KPC9zdmc+Cg==';
    img.alt = 'Image failed to load';
    onError?.();
}
/**
 * Create intersection observer for lazy loading
 */
export function createLazyLoadObserver(callback, rootMargin = '50px') {
    // SSR guard: check if window and IntersectionObserver are available
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
        // Return a lightweight mock object that implements the IntersectionObserver interface
        // This allows callers to safely call observe, unobserve, and disconnect in SSR
        return {
            observe: () => { },
            unobserve: () => { },
            disconnect: () => { },
        };
    }
    const options = {
        rootMargin,
        threshold: 0.1,
    };
    return new IntersectionObserver((entries) => {
        entries.forEach(callback);
    }, options);
}
/**
 * Generate blur hash placeholder for mobile (expo-image)
 * This is a simplified implementation - in production you'd use a blurhash library
 */
export function generateBlurHash(imageUrl) {
    // This would typically analyze the image and generate a blur hash
    // For now, return a placeholder
    return Promise.resolve('LEHV6nWB2yk8pyo0adR*.7kCMdnj');
}
